// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * React context that broadcasts carousel state (current index, can-scroll
 * flags, the Embla API, …) to subcomponents like `ArrowNavigation` and
 * `Pagination`.
 *
 * Wrap a `CarouselProvider` around any tree that needs access; consumers call
 * `useCarouselContext()` to read the current value.
 */

import { CarouselContextValue } from "@stoneage/App/Components/EmblaCarousel/models";
import React, { createContext, ReactNode, useContext } from "react";

const CarouselContext = createContext<CarouselContextValue | null>(null);

/** Read the current carousel state. Returns `null` outside a `CarouselProvider`. */
export const useCarouselContext = (): CarouselContextValue | null => {
    return useContext(CarouselContext);
};

interface CarouselProviderProps {
    children: ReactNode;
    value: CarouselContextValue;
}

/** Broadcasts carousel state to descendants. */
export const CarouselProvider: React.FC<CarouselProviderProps> = ({ children, value }) => {
    return <CarouselContext.Provider value={value}>{children}</CarouselContext.Provider>;
};

export default CarouselContext;
